import { useState } from 'react';
import AddOrgLevelModal from '../add-org-level-modal/add-org-level-modal';
import DeleteOrgLevelModal from '../delete-org-level-modal/delete-org-level-modal';
import { NextLevelKey } from '@employ-engage/data';
import { ColorPickerModal, Tooltip } from '@employ-engage/common-components';
import { ColorResult } from 'react-color';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

export interface SetupOrganizationTreeFormProps {
  nextLevelKeys: NextLevelKey[];
  refetchCompany: () => void;
}

export function SetupOrganizationTreeForm({
  nextLevelKeys = [],
  refetchCompany,
}: SetupOrganizationTreeFormProps) {
  const { t } = useTranslation(['common']);
  const [openAddOrgLevelModal, setOpenAddOrgLevelModal] = useState(false);
  const [openDeleteOrgLevelModal, setOpenDeleteOrgLevelModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<{
    color: ColorResult | string;
    id: string;
  }>({ color: '', id: '' });

  return (
    <div>
      <ul>
        {nextLevelKeys?.map?.((item, index) => {
          const isLastItem = index === nextLevelKeys.length - 1;
          const isFirstItem = index === 0;
          return (
            <li
              key={item.id}
              className="flex items-center gap-2"
              style={{
                marginLeft: `${index * 20}px`,
              }}
            >
              <button
                onClick={() => {
                  setSelectedColor({
                    color: item.color || '#000000',
                    id: item.id,
                  });
                  setSelectedLevel(item.name);
                }}
              >
                <FontAwesomeIcon
                  icon="square"
                  style={{
                    color:
                      typeof item.color === 'string'
                        ? item.color
                        : (item.color as ColorResult).hex,
                  }}
                />
              </button>
              <span
                style={{
                  color:
                    typeof item.color === 'string'
                      ? item.color
                      : item.color.hex,
                }}
              >
                {t(item.name)}
              </span>
              {isLastItem && !isFirstItem && (
                <button
                  onClick={() => {
                    setOpenDeleteOrgLevelModal(true);
                  }}
                >
                  <i className="fa-solid fa-trash cursor-pointer text-semantic-error" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mt-4">
        <Tooltip label={t('Add new organization level')}>
          <button
            onClick={() => {
              setOpenAddOrgLevelModal(true);
            }}
          >
            <i className="add-org-level-icon fa-solid fa-circle-plus cursor-pointer hover:opacity-50" />
          </button>
        </Tooltip>
      </div>
      <DeleteOrgLevelModal
        open={openDeleteOrgLevelModal}
        setOpen={setOpenDeleteOrgLevelModal}
        nextLevelKeys={nextLevelKeys}
        refetchCompany={refetchCompany}
      />
      <AddOrgLevelModal
        open={openAddOrgLevelModal}
        setOpen={setOpenAddOrgLevelModal}
        nextLevelKeys={nextLevelKeys}
        refetchCompany={refetchCompany}
      />
      <ColorPickerModal
        title={selectedLevel}
        presetColors={nextLevelKeys.map((item) => {
          return item?.color
            ? typeof item.color === 'string'
              ? item.color
              : (item.color as ColorResult).hex
            : '#000000';
        })}
        setSelectedColor={setSelectedColor}
        selectedColor={selectedColor}
        open={!!selectedColor.color && !!selectedColor.id}
        setOpen={(isOpen) =>
          setSelectedColor(
            isOpen
              ? {
                  color: selectedColor.color,
                  id: selectedColor.id,
                }
              : { color: '', id: '' }
          )
        }
        nextLevelKeys={nextLevelKeys}
        refetchCompany={refetchCompany}
      />
    </div>
  );
}

export default SetupOrganizationTreeForm;
